// Contrail V3.12.0 — read zones from KML / KMZ, GeoJSON or a zipped Shapefile (admins: Zones → Import).
// Everything runs in the browser; nothing is uploaded until the admin picks the zones and the target companies.
// Polygons (and each part of a multi-polygon) become polygon zones; points become circles (radius from the file's
// radius / radius_m attribute, otherwise the default the admin chooses). Holes are ignored. Coordinates must be WGS84
// longitude/latitude (what Google Earth, QGIS "EPSG:4326" and our own export use).
import { circle, type LngLat } from "./geo";

export type ImportZone = {
  key: string; name: string; kind: "polygon" | "circle";
  polygon: LngLat[]; center: LngLat | null; radius: number | null; color: string | null; source: string;
};
export type ImportResult = { zones: ImportZone[]; warnings: string[] };

const MAX_POINTS = 300;
const HEX = /^#?[0-9a-f]{6}$/i;

// ---------------------------------------------------------------- small helpers
const clean = (s: unknown) => String(s ?? "").replace(/\s+/g, " ").trim().slice(0, 60);
const asHex = (v: unknown) => { const s = String(v ?? "").trim(); return HEX.test(s) ? (s.startsWith("#") ? s : `#${s}`).toUpperCase() : null; };
const valid = ([x, y]: LngLat) => Number.isFinite(x) && Number.isFinite(y) && x >= -180 && x <= 180 && y >= -90 && y <= 90;
const signedArea = (r: LngLat[]) => r.reduce((a, p, i) => { const q = r[(i + 1) % r.length]; return a + (p[0] * q[1] - q[0] * p[1]); }, 0) / 2;

/** Drop a repeated closing point, round to 6 decimals, and thin very detailed rings to ≤ 300 points (Douglas–Peucker). */
export function tidyRing(raw: LngLat[]): LngLat[] {
  let r = raw.map(([x, y]) => [+x.toFixed(6), +y.toFixed(6)] as LngLat);
  r = r.filter((p, i) => i === 0 || p[0] !== r[i - 1][0] || p[1] !== r[i - 1][1]);
  if (r.length > 1 && r[0][0] === r[r.length - 1][0] && r[0][1] === r[r.length - 1][1]) r.pop();
  if (r.length <= MAX_POINTS) return r;
  let tol = 0.000005;
  let out = r;
  while (out.length > MAX_POINTS && tol < 1) { out = simplify(r, tol); tol *= 1.6; }
  return out.length >= 3 ? out : r.slice(0, MAX_POINTS);
}
function simplify(pts: LngLat[], tol: number): LngLat[] {
  const keep = new Uint8Array(pts.length); keep[0] = keep[pts.length - 1] = 1;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    let best = -1, bi = -1;
    const [ax, ay] = pts[a], [bx, by] = pts[b];
    const dx = bx - ax, dy = by - ay, len = Math.hypot(dx, dy) || 1e-12;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs(dy * pts[i][0] - dx * pts[i][1] + bx * ay - by * ax) / len;
      if (d > best) { best = d; bi = i; }
    }
    if (best > tol && bi > 0) { keep[bi] = 1; stack.push([a, bi], [bi, b]); }
  }
  return pts.filter((_, i) => keep[i]);
}

function pushPolygon(out: ImportZone[], warn: string[], name: string, ring: LngLat[], color: string | null, source: string) {
  if (!ring.every(valid)) { warn.push(`"${name}": coordinates aren't longitude/latitude (WGS84) — skipped`); return; }
  const r = tidyRing(ring);
  if (r.length < 3) { warn.push(`"${name}": fewer than 3 points — skipped`); return; }
  out.push({ key: `${source}:${out.length}`, name, kind: "polygon", polygon: r, center: null, radius: null, color, source });
}
function pushPoint(out: ImportZone[], warn: string[], name: string, p: LngLat, radius: number | null, color: string | null, source: string) {
  if (!valid(p)) { warn.push(`"${name}": coordinates aren't longitude/latitude (WGS84) — skipped`); return; }
  out.push({ key: `${source}:${out.length}`, name, kind: "circle", polygon: [], center: [+p[0].toFixed(6), +p[1].toFixed(6)],
    radius: radius && radius >= 20 && radius <= 100000 ? Math.round(radius) : null, color, source });
}
const nameOf = (props: Record<string, any> | null | undefined, fallback: string) => {
  const p = props ?? {};
  for (const k of Object.keys(p)) if (/^(name|naam|title|label|zone|zone_name)$/i.test(k) && clean(p[k])) return clean(p[k]);
  return fallback;
};
const radiusOf = (props: Record<string, any> | null | undefined) => {
  const p = props ?? {};
  for (const k of Object.keys(p)) if (/^radius(_m)?$/i.test(k)) { const n = Number(p[k]); if (n > 0) return n; }
  return null;
};
const lower = (props: Record<string, any> | null | undefined) =>
  Object.fromEntries(Object.entries(props ?? {}).map(([k, v]) => [k.toLowerCase(), v])) as Record<string, any>;
const colourOf = (props: Record<string, any> | null | undefined) => {
  const p = lower(props);
  return asHex(p.color) ?? asHex(p.colour) ?? asHex(p.stroke) ?? asHex(p.fill) ?? null;
};
/** Our own export keeps circles as 64-point polygons plus shape/centre/radius attributes: rebuild the real circle. */
const exportedCircle = (props: Record<string, any> | null | undefined): { center: LngLat; radius: number } | null => {
  const p = lower(props);
  const lat = Number(p.center_lat ?? p.ctr_lat), lng = Number(p.center_lng ?? p.ctr_lng), r = Number(p.radius_m ?? p.radius);
  return String(p.shape ?? "").toLowerCase() === "circle" && Number.isFinite(lat) && Number.isFinite(lng) && r > 0 ? { center: [lng, lat], radius: r } : null;
};

// ---------------------------------------------------------------- GeoJSON
export function readGeoJSON(text: string, source = "GeoJSON"): ImportResult {
  const zones: ImportZone[] = [], warnings: string[] = [];
  let j: any;
  try { j = JSON.parse(text); } catch { return { zones, warnings: ["This isn't a valid GeoJSON file."] }; }
  const crs = String(j?.crs?.properties?.name ?? "");
  if (crs && !/CRS84|4326/.test(crs)) warnings.push(`The file says its coordinates are in ${crs}. Only WGS84 longitude/latitude can be read.`);
  const feats: any[] = j?.type === "FeatureCollection" ? j.features ?? [] : j?.type === "Feature" ? [j] : j?.type ? [{ type: "Feature", properties: {}, geometry: j }] : [];
  let n = 0;
  const geom = (g: any, props: any, name: string) => {
    if (!g) return;
    const ec = exportedCircle(props);
    if (g.type === "Polygon" && ec) pushPoint(zones, warnings, name, ec.center, ec.radius, colourOf(props), source);
    else if (g.type === "Polygon") pushPolygon(zones, warnings, name, (g.coordinates?.[0] ?? []) as LngLat[], colourOf(props), source);
    else if (g.type === "MultiPolygon") (g.coordinates ?? []).forEach((poly: any, i: number) =>
      pushPolygon(zones, warnings, g.coordinates.length > 1 ? `${name} (${i + 1})` : name, (poly?.[0] ?? []) as LngLat[], colourOf(props), source));
    else if (g.type === "Point") pushPoint(zones, warnings, name, g.coordinates as LngLat, radiusOf(props), colourOf(props), source);
    else if (g.type === "MultiPoint") (g.coordinates ?? []).forEach((p: LngLat, i: number) => pushPoint(zones, warnings, `${name} (${i + 1})`, p, radiusOf(props), colourOf(props), source));
    else if (g.type === "GeometryCollection") (g.geometries ?? []).forEach((x: any) => geom(x, props, name));
    else warnings.push(`"${name}": ${g.type} can't be a zone — skipped`);
  };
  for (const f of feats) { n++; geom(f?.geometry, f?.properties, nameOf(f?.properties, `Zone ${n}`)); }
  return { zones, warnings };
}

// ---------------------------------------------------------------- KML
export function readKML(text: string, source = "KML"): ImportResult {
  const zones: ImportZone[] = [], warnings: string[] = [];
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.getElementsByTagName("parsererror").length) return { zones, warnings: ["This isn't a valid KML file."] };
  const kids = (el: Element, tag: string) => Array.from(el.getElementsByTagNameNS("*", tag));
  const coords = (s: string | null | undefined): LngLat[] => (s ?? "").trim().split(/\s+/).filter(Boolean)
    .map((t) => t.split(",").map(Number)).map(([x, y]) => [x, y] as LngLat);
  // shared styles: <Style id> → line colour (KML is aabbggrr)
  const styleCol = new Map<string, string>();
  for (const st of kids(doc.documentElement, "Style")) {
    const c = (kids(st, "LineStyle")[0] ?? kids(st, "PolyStyle")[0])?.getElementsByTagNameNS("*", "color")[0]?.textContent?.trim();
    if (st.getAttribute("id") && c && /^[0-9a-f]{8}$/i.test(c)) styleCol.set(st.getAttribute("id")!, `#${c.slice(6, 8)}${c.slice(4, 6)}${c.slice(2, 4)}`.toUpperCase());
  }
  let n = 0;
  for (const pm of kids(doc.documentElement, "Placemark")) {
    n++;
    const nm = clean(pm.getElementsByTagNameNS("*", "name")[0]?.textContent) || `Zone ${n}`;
    const data: Record<string, string> = {};
    for (const d of kids(pm, "Data")) data[d.getAttribute("name") ?? ""] = d.getElementsByTagNameNS("*", "value")[0]?.textContent ?? "";
    for (const d of kids(pm, "SimpleData")) data[d.getAttribute("name") ?? ""] = d.textContent ?? "";
    const styleUrl = pm.getElementsByTagNameNS("*", "styleUrl")[0]?.textContent?.replace(/^#/, "") ?? "";
    const color = colourOf(data) ?? styleCol.get(styleUrl) ?? null;
    const polys = kids(pm, "Polygon");
    polys.forEach((p, i) => {
      const outer = kids(p, "outerBoundaryIs")[0] ?? p;
      pushPolygon(zones, warnings, polys.length > 1 ? `${nm} (${i + 1})` : nm, coords(kids(outer, "coordinates")[0]?.textContent), color, source);
    });
    if (!polys.length) {
      const pts = kids(pm, "Point");
      pts.forEach((p, i) => { const c = coords(kids(p, "coordinates")[0]?.textContent)[0]; if (c) pushPoint(zones, warnings, pts.length > 1 ? `${nm} (${i + 1})` : nm, c, radiusOf(data), color, source); });
      if (!pts.length) warnings.push(`"${nm}": only lines or other shapes — skipped`);
    }
  }
  if (!n) warnings.push("No placemarks found in this KML file.");
  return { zones, warnings };
}

// ---------------------------------------------------------------- ZIP (KMZ / zipped Shapefile)
async function inflate(data: Uint8Array): Promise<Uint8Array> {
  const ds = new (globalThis as any).DecompressionStream("deflate-raw");
  const out = new Response(new Blob([data as BlobPart]).stream().pipeThrough(ds));
  return new Uint8Array(await out.arrayBuffer());
}
export async function readZip(buf: ArrayBuffer): Promise<Map<string, Uint8Array>> {
  const v = new DataView(buf), u8 = new Uint8Array(buf), files = new Map<string, Uint8Array>();
  let eocd = -1;
  for (let i = buf.byteLength - 22; i >= Math.max(0, buf.byteLength - 66000); i--) if (v.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error("This isn't a valid .zip / .kmz file.");
  const count = v.getUint16(eocd + 10, true);
  let p = v.getUint32(eocd + 16, true);
  for (let k = 0; k < count; k++) {
    if (v.getUint32(p, true) !== 0x02014b50) break;
    const method = v.getUint16(p + 10, true), csize = v.getUint32(p + 20, true);
    const nlen = v.getUint16(p + 28, true), xlen = v.getUint16(p + 30, true), clen = v.getUint16(p + 32, true), local = v.getUint32(p + 42, true);
    const name = new TextDecoder().decode(u8.subarray(p + 46, p + 46 + nlen));
    p += 46 + nlen + xlen + clen;
    if (name.endsWith("/")) continue;
    const lstart = local + 30 + v.getUint16(local + 26, true) + v.getUint16(local + 28, true);
    const raw = u8.subarray(lstart, lstart + csize);
    if (method === 0) files.set(name, raw);
    else if (method === 8) files.set(name, await inflate(raw));
  }
  return files;
}

// ---------------------------------------------------------------- Shapefile (.shp + .dbf [+ .prj])
function readDbf(b: Uint8Array): Record<string, string>[] {
  const v = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const n = v.getUint32(4, true), hlen = v.getUint16(8, true), rlen = v.getUint16(10, true);
  const fields: { name: string; len: number }[] = [];
  const dec = new TextDecoder("utf-8");
  for (let p = 32; p < hlen - 1 && b[p] !== 0x0d; p += 32) {
    fields.push({ name: dec.decode(b.subarray(p, p + 11)).replace(/\0.*$/, "").trim(), len: b[p + 16] });
  }
  const rows: Record<string, string>[] = [];
  for (let r = 0; r < n; r++) {
    let p = hlen + r * rlen + 1;   // first byte = deleted flag
    const row: Record<string, string> = {};
    for (const f of fields) { row[f.name] = dec.decode(b.subarray(p, p + f.len)).trim(); p += f.len; }
    rows.push(row);
  }
  return rows;
}
export function readShapefile(shp: Uint8Array, dbf: Uint8Array | null, prj: string | null, source = "Shapefile"): ImportResult {
  const zones: ImportZone[] = [], warnings: string[] = [];
  if (prj && /PROJCS/i.test(prj)) return { zones, warnings: ["This Shapefile uses projected coordinates. Save it as WGS84 (EPSG:4326) and try again."] };
  const attrs = dbf ? readDbf(dbf) : [];
  const v = new DataView(shp.buffer, shp.byteOffset, shp.byteLength);
  let p = 100, i = 0;
  while (p + 8 <= shp.byteLength) {
    const len = v.getInt32(p + 4, false) * 2;
    const c = p + 8, type = v.getInt32(c, true);
    const props = attrs[i] ?? {};
    const name = nameOf(props, `Zone ${i + 1}`);
    if (type === 1 || type === 11 || type === 21) {
      pushPoint(zones, warnings, name, [v.getFloat64(c + 4, true), v.getFloat64(c + 12, true)], radiusOf(props), colourOf(props), source);
    } else if ((type === 5 || type === 15 || type === 25) && exportedCircle(props)) {
      const ec = exportedCircle(props)!;
      pushPoint(zones, warnings, name, ec.center, ec.radius, colourOf(props), source);
    } else if (type === 5 || type === 15 || type === 25) {
      const nParts = v.getInt32(c + 36, true), nPts = v.getInt32(c + 40, true);
      const parts: number[] = []; for (let k = 0; k < nParts; k++) parts.push(v.getInt32(c + 44 + 4 * k, true));
      const pt0 = c + 44 + 4 * nParts;
      const rings: LngLat[][] = parts.map((s, k) => {
        const e = k + 1 < nParts ? parts[k + 1] : nPts; const r: LngLat[] = [];
        for (let q = s; q < e; q++) r.push([v.getFloat64(pt0 + 16 * q, true), v.getFloat64(pt0 + 16 * q + 8, true)]);
        return r;
      });
      // Shapefile outer rings run clockwise (negative signed area); holes run the other way and are ignored
      const outers = rings.filter((r) => signedArea(r) < 0);
      const use = outers.length ? outers : rings;
      use.forEach((r, k) => pushPolygon(zones, warnings, use.length > 1 ? `${name} (${k + 1})` : name, r, colourOf(props), source));
    } else if (type !== 0) {
      warnings.push(`"${name}": shape type ${type} (lines) can't be a zone — skipped`);
    }
    p = c + len; i++;
  }
  return { zones, warnings };
}

// ---------------------------------------------------------------- entry point
export async function readZoneFile(file: File): Promise<ImportResult> {
  const lower = file.name.toLowerCase();
  if (file.size > 20 * 1024 * 1024) return { zones: [], warnings: ["That file is over 20 MB."] };
  if (lower.endsWith(".geojson") || lower.endsWith(".json")) return readGeoJSON(await file.text(), file.name);
  if (lower.endsWith(".kml")) return readKML(await file.text(), file.name);
  if (lower.endsWith(".kmz") || lower.endsWith(".zip")) {
    const files = await readZip(await file.arrayBuffer());
    const find = (ext: string) => [...files.keys()].filter((k) => k.toLowerCase().endsWith(ext) && !k.startsWith("__MACOSX"));
    const kml = find(".kml");
    if (kml.length) return merge(await Promise.all(kml.map(async (k) => readKML(new TextDecoder().decode(files.get(k)!), k))));
    const shps = find(".shp");
    if (shps.length) return merge(shps.map((k) => {
      const base = k.slice(0, -4);
      const get = (ext: string) => files.get(base + ext) ?? files.get(base + ext.toUpperCase()) ?? null;
      const prj = get(".prj");
      return readShapefile(files.get(k)!, get(".dbf"), prj ? new TextDecoder().decode(prj) : null, k.split("/").pop()!);
    }));
    const gj = [...find(".geojson"), ...find(".json")];
    if (gj.length) return merge(gj.map((k) => readGeoJSON(new TextDecoder().decode(files.get(k)!), k)));
    return { zones: [], warnings: ["No .kml, .shp or .geojson found inside the zip."] };
  }
  return { zones: [], warnings: ["Choose a .kml, .kmz, .geojson or zipped Shapefile (.zip)."] };
}
function merge(rs: ImportResult[]): ImportResult {
  const zones = rs.flatMap((r) => r.zones).map((z, i) => ({ ...z, key: `${z.source}:${i}` }));
  return { zones, warnings: rs.flatMap((r) => r.warnings) };
}

/** Outline for the preview layer (circles drawn with the radius the admin chose). */
export const previewFeature = (z: ImportZone, defaultRadius: number) =>
  z.kind === "circle" ? circle(z.center!, z.radius ?? defaultRadius)
    : { type: "Feature" as const, properties: {}, geometry: { type: "Polygon" as const, coordinates: [[...z.polygon, z.polygon[0]]] } };
